<template>
  <div class="editTags"
    v-loading="loading"
  >
    <section class="editTags-title">
      <div class="editTags-title-title">
        <h3>EDIT TAGS</h3>
      </div>
      <div class="editTags-title-controls">
        <neb-button type="secondary"
          @click="onCancel"
        >
          Cancel
        </neb-button>
        <neb-button @click="onSave">Save</neb-button>
      </div>
    </section>
    <section class="editTags-topics">
      <edit-topics :topics="localTopics"
        @action:addTopic="onAddTopic"
        @action:removeTopic="onRemoveTopic"
      />
    </section>
    <section class="editTags-keywords">
      <edit-keywords :keywords="localKeywords"
        @action:removeKeyword="onRemoveKeyword"
        @action:addKeyword="onAddKeyword"
      />
    </section>
    <section class="editTags-properties">
      <edit-properties :assetProperties="localAssetProperties"
        @action:addValue="onAddAssetProperty"
        @action:removeValue="onRemoveAssetProperty"
      />
    </section>
  </div>
</template>

<script>
import NebButton from '@/components/Nebula/controls/Button'
import EditTopics from './EditTopics.vue'
import EditKeywords from './EditKeywords.vue'
import EditProperties from './EditProperties.vue'
import { ref, watch, inject } from '@vue/composition-api'
import { uniqBy, uniq } from 'lodash'

export default {
  name: 'EditTags',

  components: {
    NebButton,
    EditTopics,
    EditKeywords,
    EditProperties
  },

  props: {
    topics: {
      type: Array,
      default: () => []
    },
    keywords: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'action:cancel',
    'action:save'
  ],

  setup (props, { emit }) {
    const asset = inject('asset')
    const assetProperties = inject('assetProperties')

    // Topics
    const localTopics = ref([])
    const onAddTopic = v => {
      localTopics.value = uniqBy(localTopics.value.concat([v]), 'subject')
    }
    const onRemoveTopic = v => {
      localTopics.value = localTopics.value
        .filter(t => t.subject !== v.subject)
    }
    watch(() => props.topics, (newValue) => {
      localTopics.value = newValue
    })

    // Keywords
    const localKeywords = ref([])
    const onAddKeyword = v => {
      localKeywords.value = uniq(localKeywords.value.concat([v]))
    }
    const onRemoveKeyword = v => {
      localKeywords.value = localKeywords.value
        .filter(k => k !== v)
    }
    watch(() => props.keywords, newValue => {
      localKeywords.value = newValue
    })

    // Properties
    const localAssetProperties = ref([])
    const onAddAssetProperty = ({ property, value }) => {
      const obj = {
        property_id: property.pk,
        property__name: property.name,
        asset_id: asset.value.page_url_id,
        value
      }
      localAssetProperties.value = uniqBy(
        localAssetProperties.value.concat([obj]),
        v => `${v.property__name}${v.value}`
      )
    }
    const onRemoveAssetProperty = ({ property, value }) => {
      localAssetProperties.value = uniqBy(
        localAssetProperties.value
          .filter(obj => `${obj.property__name}${obj.value}` !== `${property.name}${value}`),
        v => `${v.property__name}${v.value}`
      )
    }
    watch(assetProperties.list, newValue => {
      localAssetProperties.value = newValue
    })

    const onCancel = () => {
      localTopics.value = props.topics
      localKeywords.value = props.keywords
      localAssetProperties.value = assetProperties.list.value
      emit('action:cancel')
    }
    const onSave = () => emit('action:save', {
      newAssetProperties: localAssetProperties.value
        .map(obj => ({ property_id: obj.property_id, value: obj.value })),
      newKeywords: localKeywords.value,
      newTopics: localTopics.value
    })

    return {
      localTopics,
      localKeywords,
      localAssetProperties,

      onAddTopic,
      onRemoveTopic,
      onRemoveKeyword,
      onAddKeyword,
      onAddAssetProperty,
      onRemoveAssetProperty,
      onCancel,
      onSave
    }
  }
}
</script>

<style lang="scss">
@import '../Nebula/style.scss';

.editTags {
  &-title {
    display: flex;
    justify-content: space-between;

    &-title {
      h3 {
        margin: 0;
        font-family: $defaultFont;
        font-size: 14px;
        font-weight: 300;
        color: $slate-grey;
      }
    }

    &-controls {
      display: flex;
    }
  }
}
</style>
